// Hold dialog (ID-119): client choice, request body, server error → copy, success line time. Pure.
import { CLIENT_NAME_MAX_LENGTH, CLIENT_NAME_MIN_LENGTH } from '~/constants/clients';

export type HoldClientChoice =
  | { kind: 'existing'; clientId: string; name: string; phoneE164: string | null }
  | { kind: 'quick_add'; name: string; phoneE164: string | null; phoneValid: boolean };

export type HoldDialogPhase = 'editing' | 'submitting' | 'success';

export const isClientChosen = (client: HoldClientChoice | null): boolean => {
  if (!client) return false;
  if (client.kind === 'existing') return !!client.clientId;
  const name = client.name.trim().length;
  return name >= CLIENT_NAME_MIN_LENGTH && name <= CLIENT_NAME_MAX_LENGTH && client.phoneValid && !!client.phoneE164;
};

// "Hold unit" is enabled only while editing with a client chosen and no terminal error.
export const canSubmitHold = (input: {
  phase: HoldDialogPhase;
  client: HoldClientChoice | null;
  blocked: boolean;
}): boolean => input.phase === 'editing' && !input.blocked && isClientChosen(input.client);

export type HoldRequestBody =
  | { idempotencyKey: string; clientId: string }
  | { idempotencyKey: string; clientName: string; clientPhone: string };

export const holdRequestBody = (client: HoldClientChoice, idempotencyKey: string): HoldRequestBody =>
  client.kind === 'existing'
    ? { idempotencyKey, clientId: client.clientId }
    : { idempotencyKey, clientName: client.name.trim(), clientPhone: client.phoneE164 ?? '' };

// ---------- errors ----------

export type HoldErrorCopy = {
  // i18n key under exclusiveUnits.dialog.errors, or null to show `serverMessage` as is.
  key: string | null;
  params: Record<string, number>;
  serverMessage: string | null;
  // false: this unit can't be held from this dialog (the agent closes it); true: the same attempt may be retried.
  retryable: boolean;
  // The request reached the server and got an answer: the next attempt needs a new idempotency key.
  answered: boolean;
};

type FetchLikeError = { statusCode?: number; status?: number; data?: { data?: unknown } };

const REASON_KEYS: Record<string, { key: string; retryable: boolean; needs?: 'max' | 'hours' }> = {
  unit_taken: { key: 'unitTaken', retryable: false },
  not_available: { key: 'notAvailable', retryable: false },
  agent_limit: { key: 'agentLimit', retryable: false, needs: 'max' },
  cooldown: { key: 'cooldown', retryable: false, needs: 'hours' },
  catalogue_error: { key: 'catalogue', retryable: true },
  tier_required: { key: 'plan', retryable: false },
  uae_level_required: { key: 'uaeLevel', retryable: false },
  verify_required: { key: 'phone', retryable: false },
  not_allowed_on_project: { key: 'project', retryable: false },
  staff_cannot_hold: { key: 'staff', retryable: false },
  client_not_found: { key: 'clientNotFound', retryable: true },
};

const numberParams = (data: Record<string, unknown>): Record<string, number> =>
  Object.fromEntries(
    (['max', 'hours'] as const).flatMap((k) => (typeof data[k] === 'number' ? [[k, data[k] as number]] : [])),
  );

export const holdErrorCopy = (error: unknown): HoldErrorCopy => {
  const e = (error ?? {}) as FetchLikeError;
  const status = e.statusCode ?? e.status ?? null;
  const data = (e.data?.data ?? {}) as Record<string, unknown>;
  const reason = typeof data.reason === 'string' ? data.reason : null;
  const message = typeof data.message === 'string' ? data.message : null;
  const params = numberParams(data);
  if (!status) return { key: 'network', params, serverMessage: null, retryable: true, answered: false };
  const known = reason ? REASON_KEYS[reason] : undefined;
  if (known) {
    // Without the number the server's own sentence is shown.
    const key = known.needs && params[known.needs] === undefined && message ? null : known.key;
    return { key, params, serverMessage: message, retryable: known.retryable, answered: true };
  }
  // Field errors from the quick-add client (400) carry their own message.
  if (status === 400 && message) return { key: null, params, serverMessage: message, retryable: true, answered: true };
  if (status >= 500) return { key: 'catalogue', params, serverMessage: null, retryable: true, answered: true };
  return { key: 'generic', params, serverMessage: message, retryable: true, answered: true };
};

// A 200 whose hold is not ACTIVE (an idempotent replay of an attempt that did not go through) is not a success.
export const isHeldStatus = (status: string | null | undefined): boolean => status === 'ACTIVE';

// ---------- time ----------

export const HOLD_FALLBACK_TIMEZONE = 'Asia/Dubai';

export const isIanaTimeZone = (zone: string | null | undefined): zone is string => {
  if (!zone || !zone.includes('/')) return false;
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};

// Profile timezone (IANA) when set, else the browser's, else Dubai. Old accounts store an offset string — ignored.
export const resolveHoldTimeZone = (profileZone: string | null | undefined, browserZone: string | null | undefined) => {
  if (isIanaTimeZone(profileZone)) return profileZone;
  if (isIanaTimeZone(browserZone)) return browserZone;
  return HOLD_FALLBACK_TIMEZONE;
};

// "Asia/Dubai" → "Dubai", "America/New_York" → "New York".
export const timeZoneCity = (zone: string): string => (zone.split('/').pop() ?? zone).replace(/_/g, ' ');

// "Tue 6 Oct, 14:32" (English); other locales use their own short date-time order.
export const formatHoldTime = (iso: string, timeZone: string, locale = 'en'): string => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const options: Intl.DateTimeFormatOptions = {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  if (!locale.startsWith('en')) return new Intl.DateTimeFormat(locale, options).format(at);
  const parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')}, ${get('hour')}:${get('minute')}`;
};

// One key per dialog attempt; a double click reuses it, an answered attempt gets a new one.
export const newIdempotencyKey = (): string =>
  typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

// ---------- release ----------

const RELEASE_REASON_KEYS: Record<string, string> = {
  not_active: 'notActive',
  deal_in_progress: 'dealInProgress',
  catalogue_error: 'catalogue',
  not_found: 'notActive',
};

// i18n key under exclusiveUnits.release.errors.
export const releaseErrorKey = (error: unknown): string => {
  const e = (error ?? {}) as FetchLikeError;
  const data = (e.data?.data ?? {}) as Record<string, unknown>;
  const reason = typeof data.reason === 'string' ? data.reason : '';
  if (RELEASE_REASON_KEYS[reason]) return RELEASE_REASON_KEYS[reason];
  return (e.statusCode ?? e.status) ? 'generic' : 'network';
};
